import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '@axios';
import { RootState } from '@redux/reducers';
import { Paginated } from '@models/paginated';
import '@extensions/string';
import { ThunkCallback } from '../model/thunk-callback';
import { addNotification } from '../notification';
import { translate } from '@components/i18n';
import { toQueryParameters } from '@extensions/object';
import { PaginatedResponseApi } from '@models/order/items/response';
import { CostPaginatedRequest, CostResponse } from '@models/cost';
import moment from 'moment';
import { exportReport } from '@utils/file';

export const fetchCost = createAsyncThunk<
    Paginated<CostResponse>,
    CostPaginatedRequest
>('cost/list', async (request, thunkAPI) => {
    const params = toQueryParameters({
        page: request.current - 1,
        size: request.pageSize < 10 ? 10 : request.pageSize,
        sort: request.sort || 'numeroItem,asc',
        query: request.query || '',
    });
    try {
        const response = await axios.get<PaginatedResponseApi<CostResponse>>(
            `/custo?${params}`,
        );

        const { data, status } = response;

        if (!data) {
            return {
                items: [],
                current: 0,
                total: 0,
                pageSize: 0,
            };
        } else {
            if (status === 200) {
                return {
                    items: data.content,
                    current: request.current,
                    total: response.data.totalElements,
                    pageSize: response.data.numberOfElements,
                    totalPages: response.data.totalPages,
                };
            } else {
                return thunkAPI.rejectWithValue('');
            }
        }
    } catch (e) {
        // TODO: here will be fetch's actions
        return thunkAPI.rejectWithValue(translate('general.erroListingItems'));
    }
});

export const createCost = createAsyncThunk<
    CostResponse,
    ThunkCallback<CostResponse>
>('cost/create', async (request, thunkAPI) => {
    try {
        const response = await axios.post<CostResponse>('/custo', request.data);

        const { data, status } = response;

        if (status === 201 && data) {
            thunkAPI.dispatch(
                addNotification({
                    type: 'success',
                    message: translate('general.registered'),
                    title: translate('general.success'),
                    notificationKey: request.notificationKey,
                    callback: request.onSuccess,
                }),
            );

            return data;
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
        // TODO: here will be fetch's actions
        return thunkAPI.rejectWithValue(translate('general.erroListingItems'));
    }
});

export const updateCost = createAsyncThunk<
    CostResponse,
    ThunkCallback<CostResponse>
>('cost/update', async (request, thunkAPI) => {
    try {
        const response = await axios.put<CostResponse>(
            '/custo/' + request.data.id,
            request.data,
        );

        const { data, status } = response;

        if (status === 200) {
            thunkAPI.dispatch(
                addNotification({
                    type: 'success',
                    message: translate('general.updated'),
                    title: translate('general.success'),
                    notificationKey: request.notificationKey,
                    callback: request.onSuccess,
                }),
            );

            return data;
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});

export const exportCost = createAsyncThunk(
    'cost/export',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get(`/custo/export`, {
                responseType: 'blob',
            });

            const { status, data } = response;

            const now = moment().format('YYYY-MM-DD_HH:mm:ss');

            exportReport(data, `custo_${now}.xlsx`);

            if (status === 200) {
                return;
            }
        } catch (e) {
            return thunkAPI.rejectWithValue(translate('general.errorExporting'));
        }
    },
);

export const importCost = createAsyncThunk<void, FormData>(
    'cost/import',
    async (request, thunkAPI) => {
        const notificationKey = new Date().getTime().toString();
        try {
            thunkAPI.dispatch(
                addNotification({
                    type: 'loading',
                    message: translate('general.importing'),
                    title: translate('general.importing'),
                    notificationKey,
                }),
            );

            const response = await axios.post(`/custo/import`, request);

            const { status } = response;

            if (status === 200) {
                thunkAPI.dispatch(
                    addNotification({
                        type: 'success',
                        message: translate('general.importSuccess'),
                        title: translate('general.success'),
                        notificationKey,
                    }),
                );
                thunkAPI.dispatch(fetchCost({ current: 1, pageSize: 10, query: '', sort: 'numeroItem,asc', total: 0, totalPages: 0 }));
            }
        } catch (e) {
            if (e.response?.data && e.response.data.length > 0) {
                e.response.data.forEach((error, index) => {
                    thunkAPI.dispatch(
                        addNotification({
                            type: 'error',
                            message: error.msgUsuario,
                            title: translate('general.errorT'),
                            notificationKey: index === 0 ? notificationKey : null,
                        }),
                    );
                });
            } else {
                thunkAPI.dispatch(
                    addNotification({
                        type: 'error',
                        message: translate('general.error'),
                        title: translate('general.errorT'),
                        notificationKey,
                    }),
                );
            }
        }
    },
);

export const removeCost = createAsyncThunk<number, number>(
    'cost/remove',
    async (itemId, thunkAPI) => {
        try {
            const response = await axios.delete('/custo/' + itemId);
            if (response.status === 200) {
                thunkAPI.dispatch(removeCostState(itemId));
                return Promise.resolve(itemId);
            }
            return 0;
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;
            return thunkAPI.rejectWithValue(message);
        }
    },
);

interface IUICostState {
    list: CostResponse[];
    isFetching: boolean;
    isSuccess: boolean;
    isExporting: boolean;
    isImporting: boolean;
    request: CostPaginatedRequest;
    isError: boolean;
    errorMessage?: string;
    filter: string;
}

const initialState: IUICostState = {
    list: [],
    isFetching: false,
    isSuccess: false,
    isExporting: false,
    isImporting: false,
    request: {
        pageSize: 10,
        current: 1,
        total: 0,
        query: '',
        sort: 'numeroItem,asc',
        totalPages: 0,
    },
    isError: false,
    errorMessage: '',
    filter: '',
};

const itemsSlice = createSlice({
    name: 'costSlice',
    initialState,
    reducers: {
        clearState: (state) => {
            state = { ...initialState };
            return state;
        },
        filter: (state, action: PayloadAction<string>) => {
            state.filter = action.payload;
            return state;
        },
        removeCostState: (state, action: PayloadAction<number>) => {
            state.list = state.list.filter((u) => u.id !== action.payload);
            return state;
        },
    },
    extraReducers: {
        [updateCost.fulfilled.toString()]: (
            state,
            action: PayloadAction<CostResponse>,
        ) => {
            state.list = state.list.map((u) =>
                u.id === action.payload.id ? action.payload : u,
            );
            return state;
        },

        [fetchCost.pending.toString()]: (state) => {
            state.isFetching = true;
            return state;
        },
        [fetchCost.rejected.toString()]: (state, action) => {
            state.isFetching = false;
            state.isError = true;
            state.errorMessage = action.payload;
            return state;
        },
        [fetchCost.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<Paginated<CostResponse>>,
        ) => {
            state.isFetching = false;
            state.isSuccess = true;
            state.request = {
                ...state.request,
                total: payload.total,
                pageSize: payload.pageSize,
                current: payload.current,
                totalPages: payload.totalPages,
            };
            state.list = payload.items;
            return state;
        },

        [exportCost.pending.toString()]: (state) => {
            state.isExporting = true;
            return state;
        },
        [exportCost.fulfilled.toString()]: (state) => {
            state.isExporting = false;
            return state;
        },
        [exportCost.rejected.toString()]: (state) => {
            state.isExporting = false;
            return state;
        },

        [importCost.pending.toString()]: (state) => {
            state.isImporting = true;
            return state;
        },
        [importCost.fulfilled.toString()]: (state) => {
            state.isImporting = false;
            return state;
        },
        [importCost.rejected.toString()]: (state) => {
            state.isImporting = false;
            return state;
        },
    },
});

export const costSelector = (state: RootState): IUICostState => state.cost;

export const allCostList = ({ cost }: RootState): CostResponse[] => cost.list;

export const { clearState, filter, removeCostState } = itemsSlice.actions;

export default itemsSlice.reducer;
